import React, { useState } from 'react'
import BottomMenu from './BottomMenu'

export interface RevenueValue {
  revenue?: string | null
  orders?: string | null
}

interface RevenueScaleProps {
  initialValue?: RevenueValue
  onNext: (value: RevenueValue) => void
  onBack?: () => void
}

const REVENUE_OPTIONS = [
  { value: 'under_500k', label: 'Under $500K', hint: 'Early stage' },
  { value: '500k_1m', label: '$500K – $1M', hint: 'Growing' },
  { value: '1m_5m', label: '$1M – $5M', hint: 'Established' },
  { value: '5m_20m', label: '$5M – $20M', hint: 'Scaling' },
  { value: '20m_100m', label: '$20M – $100M', hint: 'Enterprise' },
  { value: '100m_plus', label: '$100M+', hint: 'Large enterprise' },
]

const ORDER_OPTIONS = [
  { value: 'under_1k', label: 'Under 1,000', hint: 'per year' },
  { value: '1k_10k', label: '1,000 – 10,000', hint: 'per year' },
  { value: '10k_50k', label: '10,000 – 50,000', hint: 'per year' },
  { value: '50k_250k', label: '50,000 – 250,000', hint: 'per year' },
  { value: '250k_plus', label: '250,000+', hint: 'per year' },
]

const Pill: React.FC<{ selected: boolean; onClick: () => void; label: string; hint: string }> = ({ selected, onClick, label, hint }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`tw-text-left tw-rounded-xl tw-border tw-bg-white tw-px-4 tw-py-3 tw-transition hover:tw-border-slate-300 ${
      selected ? 'tw-border-indigo-500 tw-ring-2 tw-ring-indigo-200' : 'tw-border-slate-200'
    }`}
  >
    <div className="tw-font-semibold tw-text-slate-900">{label}</div>
    <div className="tw-text-xs tw-text-slate-500">{hint}</div>
  </button>
)

/** #693: revenue scale (GMV) + optional orders/year — store-path only. */
const RevenueScale: React.FC<RevenueScaleProps> = ({ initialValue = {}, onNext, onBack }) => {
  const [revenue, setRevenue] = useState<string | null>(initialValue.revenue ?? null)
  const [orders, setOrders] = useState<string | null>(initialValue.orders ?? null)

  return (
    <div className="tw-p-4 md:tw-p-8 tw-pb-32">
      <div className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-3">Annual revenue (GMV)</div>
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mb-8">
        {REVENUE_OPTIONS.map((o) => (
          <Pill key={o.value} selected={revenue === o.value} onClick={() => setRevenue(o.value)} label={o.label} hint={o.hint} />
        ))}
      </div>

      <div className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-3">
        Orders per year <span className="tw-font-normal tw-text-slate-400">(optional)</span>
      </div>
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
        {ORDER_OPTIONS.map((o) => (
          <Pill key={o.value} selected={orders === o.value} onClick={() => setOrders(o.value)} label={o.label} hint={o.hint} />
        ))}
      </div>

      <BottomMenu
        onPrev={onBack}
        onNext={() => onNext({ revenue, orders })}
        disableNext={!revenue}
      />
    </div>
  )
}

export default RevenueScale
